import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { formatMessage } from 'devextreme/localization';
import LoadPanel from 'devextreme-react/load-panel';

import LoginPage from '../login-page/LoginPage';
import SsoLoginPage from '../sso-login-page/SsoLoginPage';

const ssoPathPattern = /^\/sso\/([a-z0-9][a-z0-9-]*[a-z0-9])\/?$/i;

// The sign-in URL of a company is /sso/{loginSlug}, read before the hash router: it is a real path.
export function getSsoLoginSlug(pathname: string): string | null {
    const match = ssoPathPattern.exec(pathname);

    return match === null ? null : match[1].toLowerCase();
}

/**
 * Renders the application only for a signed-in user. Without any account in the cache it shows the sign-in page, or
 * the single sign-on page of a company when the URL is its own (/sso/{loginSlug}); while MSAL is handling a redirect
 * it shows a loader. A user who is already signed in never sees the sign-in page again: tokens are renewed silently.
 */
const AuthGate: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
    const { instance, accounts, inProgress } = useMsal();

    const [errorMessage, setErrorMessage] = useState<string>('');

    useEffect(() => {
        const callbackId = instance.addEventCallback((message: EventMessage) => {
            if (message.eventType === EventType.LOGIN_FAILURE || message.eventType === EventType.ACQUIRE_TOKEN_FAILURE) {
                console.error(message.error);
                setErrorMessage(formatMessage('LoginPage_SignInFailed'));
            }
        });

        return () => {
            if (callbackId) {
                instance.removeEventCallback(callbackId);
            }
        };
    }, [instance]);

    const isSignedIn = accounts.length > 0;

    useEffect(() => {
        // A signed-in user who lands on a company sign-in URL (bookmark, back button) gets the application address back.
        if (isSignedIn && getSsoLoginSlug(window.location.pathname) !== null) {
            window.history.replaceState(window.history.state, '', '/' + window.location.search + window.location.hash);
        }
    }, [isSignedIn]);

    if (isSignedIn) {
        return <>{children}</>;
    }

    if (inProgress !== InteractionStatus.None) {
        return <LoadPanel visible={true} />;
    }

    const loginSlug = getSsoLoginSlug(window.location.pathname);

    if (loginSlug !== null) {
        return <SsoLoginPage loginSlug={loginSlug} />;
    }

    return <LoginPage errorMessage={errorMessage} />;
};

export default AuthGate;
